@if (course$ | async; as course) {
  <div class="space-y-4">
    <div class="px-1">
      <h2 class="review-page-title">{{ 'course.review.title' | translate }}</h2>
    </div>

    <section class="clp-card">
      <div class="clp-card-body flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="min-w-0">
          <p class="review-page-subtitle truncate">{{ course.title || '-' }}</p>
        </div>
        <span class="review-status-pill" [ngClass]="getStatusBadgeClass(course.status)">
          {{ getStatusLabel(course.status) | translate }}
        </span>
      </div>
    </section>

    <div class="review-top-grid">
      <section class="clp-card">
        <div class="clp-card-body space-y-4">
          <h3 class="review-section-title">{{ 'course.general-description-tab' | translate }}</h3>

          <div class="review-grid">
            <div class="review-field">
              <span class="review-label">{{ 'course.create.name' | translate }}</span>
              <p class="review-value">{{ course.title || '-' }}</p>
            </div>

            <div class="review-field">
              <span class="review-label">{{ 'course.create.headline' | translate }}</span>
              <p class="review-value">{{ course.headline || '-' }}</p>
            </div>

            <div class="review-field">
              <span class="review-label">{{ 'course.create.instructor' | translate }}</span>
              <p class="review-value">{{ course.instructor || '-' }}</p>
            </div>
          </div>

          <div class="review-mini-grid">
            <div class="review-mini-field">
              <span class="review-label">{{ 'course.create.order' | translate }}</span>
              <p class="review-mini-value">{{ course.order ?? '-' }}</p>
            </div>

            <div class="review-mini-field">
              <span class="review-label">{{ 'course.create.hours' | translate }}</span>
              <p class="review-mini-value">{{ course.hours || '-' }}</p>
            </div>

            <div class="review-mini-field">
              <span class="review-label">{{ 'course.create.limitedDaysAccess' | translate }}</span>
              <p class="review-mini-value">{{ course.limitedDaysAccess ?? '-' }}</p>
            </div>

            <div class="review-mini-field">
              <span class="review-label">{{ 'course.create.issueCertificate' | translate }}</span>
              @if (course.issueCertificate) {
                <p class="review-mini-value">{{ 'general.yes' | translate }}</p>
              } @else {
                <p class="review-mini-value">{{ 'general.no' | translate }}</p>
              }
            </div>
          </div>

          <div class="review-richtext-stack">
            <div class="review-richtext-card">
              <span class="review-label">{{ 'course.create.summary' | translate }}</span>
              <p class="review-richtext" [innerHTML]="course.summary | stringToHtml"></p>
            </div>

            <div class="review-richtext-card">
              <span class="review-label">{{ 'course.create.about' | translate }}</span>
              <p class="review-richtext" [innerHTML]="course.about | stringToHtml"></p>
            </div>
          </div>
        </div>
      </section>

      <section class="clp-card">
        <div class="clp-card-body space-y-4">
          <h3 class="review-section-title">{{ 'course.image-tab' | translate }}</h3>
          <div class="review-image-frame">
            <img class="review-image" [src]="getImageUrl(course.picture)" [alt]="course.title || 'Course image'">
          </div>
        </div>
      </section>
    </div>

    <section class="clp-card">
      <div class="clp-card-body space-y-4">
        <h3 class="review-section-title">{{ 'course.financial-tab' | translate }}</h3>
        <div class="review-metrics-grid">
          <article class="review-metric-card">
            <span class="review-label">{{ 'course.create.fullPrice' | translate }}</span>
            @if (course.fullPrice !== null && course.fullPrice !== undefined && course.currency) {
              <p class="review-metric-value">{{ course.fullPrice.toString() | translocoCurrency: 'symbol' : { minimumFractionDigits: 2 }: course.currency }}</p>
            } @else {
              <p class="review-metric-value">-</p>
            }
          </article>

          <article class="review-metric-card">
            <span class="review-label">{{ 'course.create.discountPrice' | translate }}</span>
            @if (course.discountPrice !== null && course.discountPrice !== undefined && course.currency) {
              <p class="review-metric-value">{{ course.discountPrice.toString() | translocoCurrency: 'symbol' : { minimumFractionDigits: 2 }: course.currency }}</p>
            } @else {
              <p class="review-metric-value">-</p>
            }
          </article>

          <article class="review-metric-card">
            <span class="review-label">{{ 'course.create.currency' | translate }}</span>
            <p class="review-metric-value">{{ course.currency || '-' }}</p>
          </article>

          <article class="review-metric-card">
            <span class="review-label">{{ 'course.create.installments' | translate }}</span>
            <p class="review-metric-value">{{ course.installments ?? '-' }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="clp-card">
      <div class="clp-card-body space-y-4">
        <h3 class="review-section-title">{{ 'course.calendar-tab' | translate }}</h3>
        <div class="review-metrics-grid">
          <article class="review-metric-card">
            <span class="review-label">{{ 'course.create.launchDate' | translate }}</span>
            @if (course.launchDate) {
              <p class="review-metric-value">{{ course.launchDate | translocoDate: { dateStyle: 'medium' } }}</p>
            } @else {
              <p class="review-metric-value">-</p>
            }
          </article>

          <article class="review-metric-card">
            <span class="review-label">{{ 'course.create.salesStartDate' | translate }}</span>
            @if (course.salesStartDate) {
              <p class="review-metric-value">{{ course.salesStartDate | translocoDate: { dateStyle: 'medium' } }}</p>
            } @else {
              <p class="review-metric-value">-</p>
            }
          </article>

          <article class="review-metric-card">
            <span class="review-label">{{ 'course.create.salesEndDate' | translate }}</span>
            @if (course.salesEndDate) {
              <p class="review-metric-value">{{ course.salesEndDate | translocoDate: { dateStyle: 'medium' } }}</p>
            } @else {
              <p class="review-metric-value">-</p>
            }
          </article>
        </div>
      </div>
    </section>

    <section class="clp-card">
      <div class="clp-card-body space-y-4">
        <h3 class="review-section-title">{{ 'course.modules-tab' | translate }}</h3>

        @if (course.courseModules && course.courseModules.length > 0) {
          <div class="space-y-2.5">
            @for (module of course.courseModules; track $index) {
              <article class="review-module-card">
                <header class="review-module-header">
                  <span class="review-module-title">{{ module.title }}</span>
                  <span class="review-module-meta">
                    <span class="review-module-count">{{ module.lessons?.length ?? 0 }}</span>
                  </span>
                </header>

                <div class="review-module-body">
                  @if (module.lessons && module.lessons.length > 0) {
                    <div class="overflow-x-auto">
                      <table class="review-lesson-table">
                        <thead class="review-lesson-table-head">
                          <tr>
                            <th class="review-lesson-th">{{ 'lesson.title' | translate }}</th>
                            <th class="review-lesson-th">{{ 'course.create.status' | translate }}</th>
                            <th class="review-lesson-th">{{ 'lesson.order' | translate }}</th>
                            <th class="review-lesson-th">{{ 'lesson.create.description' | translate }}</th>
                            <th class="review-lesson-th">{{ 'lesson.create.videoType' | translate }}</th>
                            <th class="review-lesson-th">{{ 'lesson.create.videoLink' | translate }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (lesson of module.lessons; track lesson.id) {
                            <tr class="review-lesson-row">
                              <td class="review-lesson-title-cell">{{ lesson.title }}</td>
                              <td class="review-lesson-status-cell">
                                <span class="review-lesson-status-badge" [ngClass]="getLessonStatusClass(lesson.status)">
                                  @if (lesson.status) {
                                    {{ ('lesson.enum.' + lesson.status) | translate }}
                                  } @else {
                                    -
                                  }
                                </span>
                              </td>
                              <td class="review-lesson-order-cell">{{ lesson.order }}</td>
                              <td class="review-lesson-description-cell">
                                <span class="review-lesson-description-text" [title]="lesson.description || ''">{{ lesson.description || '-' }}</span>
                              </td>
                              <td class="review-lesson-video-type-cell">{{ getVideoTypeLabel(lesson.videoType) }}</td>
                              <td class="review-lesson-video-link-cell">
                                <span class="review-lesson-link-text" [title]="lesson.videoLink || ''">{{ lesson.videoLink || '-' }}</span>
                              </td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  } @else {
                    <p class="review-module-empty-lessons">{{ 'lesson.noLessons' | translate }}</p>
                  }
                </div>
              </article>
            }
          </div>
        } @else {
          <div class="review-empty-state">
            <i class="fa-solid fa-layer-group text-2xl text-slate-500"></i>
            <p class="mt-2 text-sm text-slate-300">{{ 'courseModule.noModules' | translate }}</p>
          </div>
        }
      </div>
    </section>
  </div>
}
